import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import {useAlert} from "../context/AlertContext";
import {
    deleteCourseLogo,
    getCourseLogoById,
    getCourseLogos,
    updateCourseLogo,
} from "../api/api";

import {CourseLogo} from "../types";
import AppTableHeaderCell from "./AppTableHeaderCell";
import AppModalBox from "./AppModalBox";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import CourseLogoPreview from "./CourseLogoPreview";

interface Props {
    onAdd: () => void;
    refreshTrigger: boolean;
    refreshCourseLogos: () => void;
}

const actionButtonSx = (theme: any) => ({
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    "&:hover": {
        backgroundColor: theme.palette.action.selected,
    },
});

const CourseLogosList = ({
                             onAdd,
                             refreshTrigger,
                             refreshCourseLogos,
                         }: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();

    const [courseLogos, setCourseLogos] = useState<CourseLogo[]>([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 10;

    const [searchInput, setSearchInput] = useState("");
    const [searchName, setSearchName] = useState("");

    const [loading, setLoading] = useState(false);

    const [viewingLogo, setViewingLogo] =
        useState<CourseLogo | null>(null);
    const [viewModalLoading, setViewModalLoading] =
        useState(false);

    const [editingLogo, setEditingLogo] =
        useState<CourseLogo | null>(null);
    const [editModalLoading, setEditModalLoading] =
        useState(false);
    const [editSaving, setEditSaving] =
        useState(false);

    const [editName, setEditName] = useState("");
    const [editContent, setEditContent] = useState("");

    const [logoToDelete, setLogoToDelete] =
        useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] =
        useState(false);

    const fetchCourseLogosPage = async () => {
        try {
            setLoading(true);

            const response = await getCourseLogos(
                page - 1,
                pageSize,
                searchName
            );

            setCourseLogos(response.logos);
            setTotalPages(response.totalPages);
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courselogoslist.errors.loadFailed"),
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourseLogosPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, refreshTrigger, searchName]);

    const handleSearch = () => {
        setPage(1);
        setSearchName(searchInput.trim());
    };

    const openView = async (logo: CourseLogo) => {
        try {
            setViewingLogo(logo);
            setViewModalLoading(true);

            const details = await getCourseLogoById(logo.id);
            setViewingLogo(details);
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courselogoslist.errors.loadDetailsFailed"),
                "error"
            );

            setViewingLogo(null);
        } finally {
            setViewModalLoading(false);
        }
    };

    const closeViewModal = () => {
        setViewingLogo(null);
        setViewModalLoading(false);
    };

    const openEdit = async (logo: CourseLogo) => {
        try {
            setEditingLogo(logo);
            setEditModalLoading(true);

            const details = await getCourseLogoById(logo.id);

            setEditingLogo(details);
            setEditName(details.name ?? "");
            setEditContent(details.content ?? "");
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courselogoslist.errors.loadDetailsFailed"),
                "error"
            );

            setEditingLogo(null);
        } finally {
            setEditModalLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditingLogo(null);
        setEditModalLoading(false);
        setEditSaving(false);
        setEditName("");
        setEditContent("");
    };

    const handleEditLogo = async (
        e: React.FormEvent
    ) => {
        e.preventDefault();

        if (!editingLogo) {
            return;
        }

        if (!editName.trim()) {
            showAlert(
                t("courselogoslist.errors.nameRequired"),
                "error"
            );
            return;
        }

        if (!editContent.trim()) {
            showAlert(
                t("courselogoslist.errors.contentRequired"),
                "error"
            );
            return;
        }

        try {
            setEditSaving(true);

            await updateCourseLogo(editingLogo.id, {
                name: editName.trim(),
                content: editContent.trim(),
            });

            showAlert(
                t("courselogoslist.alerts.updated"),
                "success"
            );

            closeEditModal();
            refreshCourseLogos();
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courselogoslist.errors.updateFailed"),
                "error"
            );
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteLogo = async () => {
        if (logoToDelete === null) {
            return;
        }

        try {
            setDeleteLoading(true);

            await deleteCourseLogo(logoToDelete);

            showAlert(
                t("courselogoslist.alerts.deleted"),
                "success"
            );

            setLogoToDelete(null);

            if (courseLogos.length === 1 && page > 1) {
                setPage((previousPage) => previousPage - 1);
            } else {
                refreshCourseLogos();
            }
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courselogoslist.errors.deleteFailed"),
                "error"
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            {loading ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 8,
                    }}
                >
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor:
                        theme.palette.background.paper,
                        border:
                            `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: {
                                xs: "stretch",
                                sm: "center",
                            },
                            gap: 1.5,
                            px: {
                                xs: 1.5,
                                md: 3,
                            },
                            py: 2,
                            flexDirection: {
                                xs: "column",
                                sm: "row",
                            },
                            backgroundColor:
                            theme.palette.background.paper,
                            borderBottom:
                                `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                            }}
                        >
                            <TextField
                                type="search"
                                size="small"
                                placeholder={t(
                                    "courselogoslist.searchByName"
                                )}
                                value={searchInput}
                                onChange={(e) =>
                                    setSearchInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleSearch();
                                    }
                                }}
                                sx={{
                                    width: {
                                        xs: "100%",
                                        sm: 320,
                                    },
                                    flexGrow: {
                                        xs: 1,
                                        sm: 0,
                                    },
                                }}
                            />

                            <IconButton
                                onClick={handleSearch}
                                disabled={loading}
                                aria-label={t("common.search")}
                                sx={(theme) => ({
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor:
                                    theme.palette.action.hover,
                                    color:
                                    theme.palette.text.primary,
                                    flexShrink: 0,
                                    "&:hover": {
                                        backgroundColor:
                                        theme.palette.action.selected,
                                    },
                                })}
                            >
                                <SearchIcon fontSize="small"/>
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                flexGrow: 1,
                                display: {
                                    xs: "none",
                                    sm: "block",
                                },
                            }}
                        />

                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                    "space-between",
                                gap: 1,
                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },
                                px: {
                                    xs: 1.25,
                                    sm: 0,
                                },
                                py: {
                                    xs: 1,
                                    sm: 0,
                                },
                                borderRadius: {
                                    xs: 2.5,
                                    sm: 0,
                                },
                                backgroundColor: {
                                    xs: theme.palette.action.hover,
                                    sm: "transparent",
                                },
                            })}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{fontWeight: 700}}
                            >
                                {t("common.pageOf", {
                                    page,
                                    totalPages,
                                })}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontWeight: 700,
                                    display: {
                                        xs: "block",
                                        sm: "none",
                                    },
                                }}
                            >
                                {t("courselogoslist.shown", {
                                    count: courseLogos.length,
                                })}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onAdd}
                            aria-label={t(
                                "courselogoslist.addLogo"
                            )}
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                backgroundColor:
                                theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                display: {
                                    xs: "none",
                                    sm: "inline-flex",
                                },
                                "&:hover": {
                                    backgroundColor:
                                    theme.palette.action.selected,
                                },
                            })}
                        >
                            <AddIcon fontSize="small"/>
                        </IconButton>

                        <Button
                            onClick={onAdd}
                            variant="contained"
                            startIcon={
                                <AddCircleOutlineIcon
                                    fontSize="small"
                                />
                            }
                            sx={{
                                display: {
                                    xs: "inline-flex",
                                    sm: "none",
                                },
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 800,
                                py: 0.95,
                            }}
                        >
                            {t("courselogoslist.addLogo")}
                        </Button>
                    </Box>

                    {courseLogos.length === 0 ? (
                        <Box
                            sx={{
                                py: 10,
                                px: 3,
                                textAlign: "center",
                            }}
                        >
                            <ImageOutlinedIcon
                                sx={{
                                    fontSize: 48,
                                    color: "text.secondary",
                                    mb: 1,
                                }}
                            />

                            <Typography
                                variant="h6"
                                sx={{mb: 1}}
                            >
                                {t(
                                    "courselogoslist.noLogosFound"
                                )}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {t(
                                    "courselogoslist.noLogosDescription"
                                )}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: {
                                        xs: "none",
                                        md: "block",
                                    },
                                    width: "100%",
                                    overflowX: "auto",
                                }}
                            >
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "separate",
                                        borderSpacing: 0,
                                    }}
                                >
                                    <thead>
                                    <tr>
                                        <AppTableHeaderCell>
                                            ID
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t(
                                                "courselogoslist.logo"
                                            )}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t(
                                                "courselogoslist.name"
                                            )}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell align="right">
                                            {t("common.actions")}
                                        </AppTableHeaderCell>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {courseLogos.map((logo) => (
                                        <tr key={logo.id}>
                                            <td
                                                style={{
                                                    padding:
                                                        "14px 16px",
                                                }}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={`#${logo.id}`}
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            </td>

                                            <td
                                                style={{
                                                    padding:
                                                        "14px 16px",
                                                }}
                                            >
                                                <CourseLogoPreview
                                                    content={
                                                        logo.content
                                                    }
                                                    name={logo.name}
                                                    size={35}
                                                />
                                            </td>

                                            <td
                                                style={{
                                                    padding:
                                                        "14px 16px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        wordBreak:
                                                            "break-word",
                                                    }}
                                                >
                                                    {logo.name || "—"}
                                                </Typography>
                                            </td>

                                            <td
                                                style={{
                                                    padding:
                                                        "14px 16px",
                                                    textAlign: "right",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:
                                                            "inline-flex",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={t(
                                                            "common.view"
                                                        )}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                openView(
                                                                    logo
                                                                )
                                                            }
                                                            aria-label={t(
                                                                "common.view"
                                                            )}
                                                            sx={
                                                                actionButtonSx
                                                            }
                                                        >
                                                            <VisibilityOutlinedIcon
                                                                fontSize="small"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip
                                                        title={t(
                                                            "common.edit"
                                                        )}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                openEdit(
                                                                    logo
                                                                )
                                                            }
                                                            aria-label={t(
                                                                "common.edit"
                                                            )}
                                                            sx={
                                                                actionButtonSx
                                                            }
                                                        >
                                                            <EditIcon
                                                                fontSize="small"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip
                                                        title={t(
                                                            "common.delete"
                                                        )}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setLogoToDelete(
                                                                    logo.id
                                                                )
                                                            }
                                                            aria-label={t(
                                                                "common.delete"
                                                            )}
                                                            sx={
                                                                actionButtonSx
                                                            }
                                                        >
                                                            <DeleteIcon
                                                                fontSize="small"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Box>
                            <Box
                                sx={{
                                    display: {
                                        xs: "flex",
                                        md: "none",
                                    },
                                    flexDirection: "column",
                                    gap: 1.25,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        background:
                                            theme.palette.mode ===
                                            "dark"
                                                ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                                : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                        border:
                                            `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent:
                                                "space-between",
                                            gap: 1,
                                        }}
                                    >
                                        <Box sx={{minWidth: 0}}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 900,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {t(
                                                    "courselogoslist.adminLogos"
                                                )}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {t(
                                                    "courselogoslist.adminLogosDescription"
                                                )}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={t(
                                                "courselogoslist.shown",
                                                {
                                                    count:
                                                    courseLogos.length,
                                                }
                                            )}
                                            sx={(theme) => ({
                                                fontWeight: 800,
                                                backgroundColor:
                                                theme.palette.action.hover,
                                                flexShrink: 0,
                                            })}
                                        />
                                    </Box>
                                </Box>

                                {courseLogos.map((logo) => (
                                    <Box
                                        key={logo.id}
                                        sx={(theme) => ({
                                            p: 1.5,
                                            borderRadius: 3,
                                            border:
                                                `1px solid ${theme.palette.divider}`,
                                            backgroundColor:
                                            theme.palette.background.paper,
                                            boxShadow:
                                                theme.palette.mode ===
                                                "dark"
                                                    ? "0 14px 32px rgba(0,0,0,0.32)"
                                                    : "0 14px 32px rgba(15,23,42,0.07)",
                                            overflow: "hidden",
                                            position: "relative",
                                            "&:before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                width: 4,
                                                backgroundColor:
                                                theme.palette.primary.main,
                                            },
                                        })}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.25,
                                            }}
                                        >
                                            <CourseLogoPreview
                                                content={logo.content}
                                                name={logo.name}
                                                size={72}
                                            />

                                            <Box
                                                sx={{
                                                    minWidth: 0,
                                                    flexGrow: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        lineHeight: 1.35,
                                                        wordBreak:
                                                            "break-word",
                                                    }}
                                                >
                                                    {logo.name || "—"}
                                                </Typography>

                                                <Chip
                                                    size="small"
                                                    label={`${t(
                                                        "courselogoslist.logoId"
                                                    )}: ${logo.id}`}
                                                    sx={(theme) => ({
                                                        mt: 1,
                                                        fontWeight: 800,
                                                        backgroundColor:
                                                        theme.palette.action.hover,
                                                    })}
                                                />
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "1fr 1fr",
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                    openView(logo)
                                                }
                                                startIcon={
                                                    <VisibilityOutlinedIcon
                                                        fontSize="small"
                                                    />
                                                }
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform:
                                                        "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.view")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                    openEdit(logo)
                                                }
                                                startIcon={
                                                    <EditIcon
                                                        fontSize="small"
                                                    />
                                                }
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform:
                                                        "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.edit")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                onClick={() =>
                                                    setLogoToDelete(
                                                        logo.id
                                                    )
                                                }
                                                startIcon={
                                                    <DeleteIcon
                                                        fontSize="small"
                                                    />
                                                }
                                                sx={{
                                                    gridColumn: "1 / -1",
                                                    borderRadius: 2,
                                                    textTransform:
                                                        "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.delete")}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                }}
            >
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <DeleteConfirmationModal
                open={logoToDelete !== null}
                onClose={() =>
                    deleteLoading
                        ? null
                        : setLogoToDelete(null)
                }
                onConfirm={handleDeleteLogo}
            />

            {/* View modal */}
            <Modal
                open={viewingLogo !== null}
                onClose={closeViewModal}
            >
                <AppModalBox
                    sx={{
                        margin: "auto",
                        width: {
                            xs: "calc(100vw - 24px)",
                            sm: 680,
                        },
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: {
                            xs: 2,
                            sm: 3,
                        },
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{fontWeight: 900}}
                    >
                        {t("courselogoslist.viewLogo")}
                    </Typography>

                    {viewModalLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 5,
                            }}
                        >
                            <CircularProgress/>
                        </Box>
                    ) : viewingLogo && (
                        <>
                            <Box
                                sx={(theme) => ({
                                    p: 3,
                                    borderRadius: 3,
                                    border:
                                        `1px solid ${theme.palette.divider}`,
                                    backgroundColor:
                                    theme.palette.action.hover,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                })}
                            >
                                <CourseLogoPreview
                                    content={viewingLogo.content}
                                    name={viewingLogo.name}
                                    size={220}
                                />

                                <Box sx={{textAlign: "center"}}>
                                    <Typography
                                        variant="h6"
                                        sx={{fontWeight: 900}}
                                    >
                                        {viewingLogo.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        ID: {viewingLogo.id}
                                    </Typography>
                                </Box>
                            </Box>

                            <TextField
                                label={t(
                                    "courselogoslist.rawContent"
                                )}
                                value={viewingLogo.content}
                                fullWidth
                                multiline
                                minRows={7}
                                maxRows={14}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                    },
                                }}
                                sx={{
                                    "& textarea": {
                                        fontFamily: "monospace",
                                        fontSize: 13,
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                onClick={closeViewModal}
                                sx={{
                                    alignSelf: {
                                        xs: "stretch",
                                        sm: "flex-end",
                                    },
                                    textTransform: "none",
                                    fontWeight: 800
                                }}
                            >
                                {t("common.close")}
                            </Button>
                        </>
                    )}
                </AppModalBox>
            </Modal>

            {/* Edit modal */}
            <Modal
                open={editingLogo !== null}
                onClose={closeEditModal}
            >
                <AppModalBox
                    sx={{
                        margin: "auto",
                        width: {
                            xs: "calc(100vw - 24px)",
                            sm: 680,
                        },
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: {
                            xs: 2,
                            sm: 3,
                        },
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{fontWeight: 900}}
                    >
                        {t("courselogoslist.editLogo")}
                    </Typography>

                    {editModalLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 5,
                            }}
                        >
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <form onSubmit={handleEditLogo}>
                            <TextField
                                label={t(
                                    "courselogoslist.name"
                                )}
                                fullWidth
                                required
                                value={editName}
                                onChange={(e) =>
                                    setEditName(e.target.value)
                                }
                                disabled={editSaving}
                                inputProps={{
                                    maxLength: 255,
                                }}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t(
                                    "courselogoslist.content"
                                )}
                                fullWidth
                                required
                                multiline
                                minRows={8}
                                maxRows={15}
                                value={editContent}
                                onChange={(e) =>
                                    setEditContent(e.target.value)
                                }
                                disabled={editSaving}
                                sx={{
                                    mb: 2,
                                    "& textarea": {
                                        fontFamily: "monospace",
                                        fontSize: 13,
                                    },
                                }}
                            />

                            <Box
                                sx={(theme) => ({
                                    mb: 2,
                                    p: 2,
                                    borderRadius: 3,
                                    border:
                                        `1px solid ${theme.palette.divider}`,
                                    backgroundColor:
                                    theme.palette.action.hover,
                                })}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1.5,
                                        fontWeight: 800,
                                    }}
                                >
                                    {t(
                                        "courselogoslist.preview"
                                    )}
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <CourseLogoPreview
                                        name={editName}
                                        content={editContent}
                                        size={160}
                                    />
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                    flexDirection: {
                                        xs: "column-reverse",
                                        sm: "row",
                                    },
                                }}
                            >
                                <Button
                                    onClick={closeEditModal}
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        width: {
                                            xs: "100%",
                                            sm: "auto",
                                        },
                                    }}
                                >
                                    {t("common.cancel")}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 800,
                                        width: {
                                            xs: "100%",
                                            sm: "auto",
                                        },
                                    }}
                                >
                                    {editSaving
                                        ? t(
                                            "courselogoslist.saving"
                                        )
                                        : t(
                                            "courselogoslist.saveChanges"
                                        )
                                    }
                                </Button>
                            </Box>
                        </form>
                    )}
                </AppModalBox>
            </Modal>
        </>
    );
};

export default CourseLogosList;